import { formatNumber } from '@angular/common';
import { ChangeDetectorRef, DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Asset, ExecutionType, InstrumentType, Position } from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import {
  FormCache,
  OrderFormGroup,
  OrderOptionsFormGroup,
  OrderQuantityFormGroup,
  QuantityType,
  TradeFacade,
  TradeService,
  TrailingType,
} from '@zero/trade/domain';
import { TradeCreateValidators } from '@zero/trade/ui-validators';
import { isEmpty } from 'lodash-es';
import { combineLatest, firstValueFrom } from 'rxjs';

@Directive()
export class TradeCreatePageBase {
  readonly #cdr = inject(ChangeDetectorRef);

  public form?: FormGroup<OrderFormGroup>;
  public hasTradingSession?: boolean;
  public setupComplete = false;
  public positionInSelectedAsset?: Position;
  public mode?: QuantityType;
  public assetNotFound = false;
  public typedValueAsString = '';

  protected readonly tradeFacade = inject(TradeFacade);

  readonly #TRADE_MODE_CACHE_KEY = 'trade-mode';

  protected readonly formBuilder = inject(FormBuilder);
  public readonly tradeService = inject(TradeService);
  protected readonly searchService = inject(AssetSearchService);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  protected readonly tradeCreateValidators = inject(TradeCreateValidators);
  protected readonly destroyRef = inject(DestroyRef);

  public reset(): void {
    this.tradeService.reset();
    this.init();
  }

  public init(): void {
    this.form = new FormGroup<OrderFormGroup>(
      {
        direction: new FormControl('buy', [Validators.required]),
        execType: new FormControl('market'),
        orderValue: new FormControl(null),
        quantityForm: new FormGroup({
          quantity: new FormControl(null, [
            Validators.required,
            this.tradeCreateValidators.validateQuantity(() => this.positionInSelectedAsset),
          ]),
          validToDate: new FormControl(null),
        }),
        optionsForm: new FormGroup({
          stopPrice: new FormControl(null),
          stopLimitPrice: new FormControl(null),
          limitPrice: new FormControl(null),
          stopLoss: new FormControl(null),
          takeProfit: new FormControl(null),
          isEditOrder: new FormControl(false),
          initialStopPrice: new FormControl(null),
          trailingAbs: new FormControl(null),
          trailingRel: new FormControl(null),
          trailingType: new FormControl('rel' as TrailingType),
        }),
      },
      {
        validators: [
          this.tradeCreateValidators.validateExperienceAndFtg(),
          this.tradeCreateValidators.validateDerivativeState(),
          this.tradeCreateValidators.validateMinimumOrderValue(() => this.positionInSelectedAsset),
        ],
      },
    );
    this.#cdr.detectChanges();
    this.#presetMode();
  }

  public async preset(useCache = false): Promise<void> {
    if (useCache) {
      const cache = await firstValueFrom(this.tradeService.cache$);
      if (cache) {
        this.presetByCache(cache);
      }
      return;
    }

    const { queryParams } = this.activatedRoute;
    queryParams.subscribe(async (params: Params) => {
      const { isin, useCache } = params;
      if (isin) {
        await this.#presetByUrlParams(params);
      } else if (useCache) {
        const cache = await firstValueFrom(this.tradeService.cache$);
        if (cache) {
          this.presetByCache(cache);
        }
      }
    });
  }

  public destroy(): void {
    this.typedValueAsString = '';
    this.tradeService.cacheForm(this.form);
    this.tradeService.reset();
  }

  public async assetSelected(asset: Asset): Promise<void> {
    this.#resetOptionFormControls();
    this.tradeService.reset();
    this.tradeService.updateInstrument(asset);
    this.#subscribeToOrderVolumeCalculation();

    if (asset) {
      this.positionInSelectedAsset = await this.tradeService.getPositionInPortfolio(asset.isin);
      this.tradeService.loadBuyingPower(asset.isin);
      this.#subscribeToFormValidation();
      this.assetNotFound = false;
      this.#setAdditionalAssetValidators(asset);
    }
  }

  public changeExecType = (execType: ExecutionType): void => {
    this.form.patchValue({ execType });
    this.#setAdditionalValidators(execType);
    this.#resetOptionFormControls();
  };

  public setTradingSessionState = (hasTradingSession: boolean): void => {
    this.hasTradingSession = hasTradingSession;
    this.setupComplete = true;
  };

  protected submit(): void {
    this.tradeService.cacheForm(this.form);
    const { value: quantity } = this.quantityForm.get('quantity');
    const { value: execType } = this.form.get('execType');
    const isFraction = Number(quantity) % 1 !== 0;
    const instrumentType = this.tradeService.asset?.value?.instrumentType;
    const isMarketOrder = execType === 'market';
    const fractionableInstrumentTypes = [InstrumentType.Etf, InstrumentType.Share, InstrumentType.Funds];
    const isCombinedOrder = isFraction && isMarketOrder && fractionableInstrumentTypes.includes(instrumentType);
    isCombinedOrder ? this.tradeService.validateCombinedOrder() : this.tradeService.verifyOrder();
  }

  public get quantityForm(): FormGroup<OrderQuantityFormGroup> {
    return this.form.get('quantityForm') as FormGroup<OrderQuantityFormGroup>;
  }

  public get optionsForm(): FormGroup<OrderOptionsFormGroup> {
    return this.form.get('optionsForm') as FormGroup<OrderOptionsFormGroup>;
  }

  public get directionFormControl() {
    return this.form.get('direction');
  }

  public get execTypeFormControl() {
    return this.form.get('execType');
  }

  readonly #presetByUrlParams = async (params: Params): Promise<void> => {
    const { isin } = params;
    if (!isin) {
      return;
    }

    const asset = await firstValueFrom(this.searchService.loadAssetByIsin(isin));
    if (!asset || Object.keys(asset).length === 0) {
      this.assetNotFound = true;
      return;
    }

    await this.assetSelected(asset);
    const {
      direction,
      quantity: quantityParam,
      limitPrice,
      stopPrice,
      stopLimitPrice,
      validity,
      stopLoss,
      takeProfit,
      editOrder,
      trailingAbs,
      trailingRel,
      initialStopPrice,
    } = params;
    let { execType } = params;

    const quantity = this.#presetQuantity(quantityParam, asset?.qtyScale);
    if (quantity > 0) {
      this.mode = 'quantity';
    } else {
      this.#presetMode();
    }

    const isSellOrder = direction === 'sell';
    const isValidSellOrder = isSellOrder && this.positionInSelectedAsset;

    this.form.patchValue({
      quantityForm: {
        quantity: this.typedValueAsString ? this.typedValueAsString : undefined,
        validToDate: validity ? new Date(validity) : undefined,
      },
      direction: isValidSellOrder ? 'sell' : 'buy',
    });

    if (execType) {
      execType = isSellOrder && !isValidSellOrder ? 'market' : execType;
      this.form.patchValue({
        execType,
      });

      const optionsForm = this.form.get('optionsForm') as FormGroup<OrderOptionsFormGroup>;
      optionsForm.patchValue({
        isEditOrder: editOrder === 'true',
      });
      switch (execType) {
        case 'limit':
          optionsForm.patchValue({
            limitPrice: !isNaN(limitPrice) ? Number(limitPrice) : undefined,
          });
          break;
        case 'stop':
          optionsForm.patchValue({
            stopPrice: !isNaN(stopPrice) ? Number(stopPrice) : undefined,
          });

          if (stopLimitPrice) {
            optionsForm.patchValue({
              stopLimitPrice: !isNaN(stopLimitPrice) ? Number(stopLimitPrice) : undefined,
            });
          }
          break;
        case 'stoplimit':
          this.form.patchValue({
            execType: 'stop',
          });

          optionsForm.patchValue({
            stopPrice: !isNaN(stopPrice) ? Number(stopPrice) : undefined,
            stopLimitPrice: !isNaN(stopLimitPrice) ? Number(stopLimitPrice) : undefined,
          });
          break;
        case 'oco':
          isValidSellOrder &&
            optionsForm.patchValue({
              stopLoss: !isNaN(stopLoss) ? Number(stopLoss) : undefined,
              takeProfit: !isNaN(takeProfit) ? Number(takeProfit) : undefined,
            });
          break;
        case 'tsl':
          isValidSellOrder &&
            optionsForm.patchValue({
              trailingAbs: !isNaN(trailingAbs) ? trailingAbs : undefined,
              trailingRel: !isNaN(trailingRel) ? trailingRel : undefined,
              trailingType: !isNaN(trailingAbs) && trailingAbs > 0 ? 'abs' : 'rel',
              initialStopPrice,
            });
          break;
        default:
          optionsForm.patchValue({
            limitPrice: undefined,
            stopPrice: undefined,
            stopLimitPrice: undefined,
            stopLoss: undefined,
            takeProfit: undefined,
            isEditOrder: false,
            initialStopPrice: undefined,
            trailingAbs: undefined,
            trailingRel: undefined,
          });
          break;
      }
    }
    this.#setAdditionalValidators(execType);
    this.quantityForm.get('quantity')?.updateValueAndValidity();
  };

  public presetByCache = async (cache: FormCache): Promise<void> => {
    const { asset, form } = cache;
    const { value: quantityForm } = form.get('quantityForm');
    const { value: optionsForm } = form.get('optionsForm');
    const { value: direction } = form.get('direction');
    const { value: execType } = form.get('execType');
    const { value: orderValue } = form.get('orderValue');
    this.mode = 'quantity';

    this.#presetQuantity(quantityForm.quantity, asset?.qtyScale);

    await this.assetSelected(asset);
    this.form.patchValue({
      direction,
      execType,
      orderValue,
    });
    this.form.patchValue({
      quantityForm,
      optionsForm,
    });
    this.#setAdditionalValidators(execType);
    this.quantityForm.get('quantity')?.updateValueAndValidity();
    this.form.updateValueAndValidity();
  };

  public presetByInput = async (isin: string): Promise<void> => {
    const asset = await firstValueFrom(this.searchService.loadAssetByIsin(isin));
    if (isEmpty(asset)) {
      this.assetNotFound = true;
      return;
    }

    await this.assetSelected(asset);
  };

  public changeMode = (mode: QuantityType): void => {
    this.mode = mode;
    if (this.execTypeFormControl.value === 'market') {
      localStorage.setItem(this.#TRADE_MODE_CACHE_KEY, mode);
    }
  };

  protected removeComma = (value: string): string => this.#replaceAll(value, ',', '');

  readonly #replaceAll = (string: string, search: string, replace: string): string => string.split(search).join(replace);

  readonly #presetQuantity = (quantityParam: string | number, qtyScale?: number): number => {
    if (isNaN(quantityParam as number)) {
      return 0;
    }

    const quantity = Number(quantityParam);
    qtyScale = qtyScale || 3;
    this.typedValueAsString = Number.isInteger(quantity)
      ? quantityParam.toString()
      : this.removeComma(formatNumber(quantity, 'en', `1.0-${qtyScale}`));

    return quantity;
  };

  readonly #presetMode = (): void => {
    if (this.execTypeFormControl.value !== 'market') {
      return;
    }

    const item = localStorage.getItem(this.#TRADE_MODE_CACHE_KEY);
    this.mode = (['quantity', 'value'].includes(item) ? item : 'value') as QuantityType;
  };

  readonly #setAdditionalValidators = (execType: ExecutionType): void => {
    if (!this.directionFormControl.value || !execType) {
      return;
    }

    const limitPrice = this.optionsForm.get('limitPrice');
    const stopLimitPrice = this.optionsForm.get('stopLimitPrice');
    const stopPrice = this.optionsForm.get('stopPrice');
    const stopLoss = this.optionsForm.get('stopLoss');
    const takeProfit = this.optionsForm.get('takeProfit');
    const trailingAbs = this.optionsForm.get('trailingAbs');
    const trailingRel = this.optionsForm.get('trailingRel');

    limitPrice.setValidators(null);
    stopPrice.setValidators(null);
    stopLimitPrice.setValidators(null);
    stopLoss.setValidators(null);
    takeProfit.setValidators(null);
    trailingAbs.setValidators(null);
    trailingRel.setValidators(null);

    this.optionsForm.clearValidators();

    switch (execType) {
      case 'market':
      case 'fraction':
        break;
      case 'limit':
        limitPrice.setValidators([Validators.required, Validators.min(0.000001)]);
        break;
      case 'stop':
      case 'stoplimit':
        this.optionsForm.setValidators([this.tradeCreateValidators.validateStopLimitPrice()]);

        stopPrice.setValidators([Validators.required, Validators.min(0.000001)]);
        if (this.directionFormControl.value === 'buy') {
          stopLimitPrice.setValidators([Validators.required, Validators.min(0.000001)]);
        }
        break;
      case 'oco':
        stopLoss.setValidators([Validators.required, Validators.min(0.000001)]);
        takeProfit.setValidators([Validators.required, Validators.min(0.000001)]);
        this.optionsForm.setValidators([this.tradeCreateValidators.validateOco()]);
        break;
      case 'tsl':
        trailingAbs.setValidators([Validators.min(0)]);
        trailingRel.setValidators([Validators.min(0.000001), Validators.max(100)]);
        this.optionsForm.setValidators([this.tradeCreateValidators.validateTsl()]);
        break;
      default:
        throw new Error(`unknown execType: ${execType}`);
    }

    this.optionsForm.updateValueAndValidity();
  };

  readonly #subscribeToFormValidation = (): void => {
    this.form.updateValueAndValidity();
    this.tradeService.quote$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.form.updateValueAndValidity());
    this.optionsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.form.updateValueAndValidity();
      this.quantityForm.updateValueAndValidity();
    });
  };

  readonly #resetOptionFormControls = (): void => {
    this.optionsForm.patchValue({
      limitPrice: null,
      stopLimitPrice: null,
      stopPrice: null,
      stopLoss: null,
      isEditOrder: false,
      takeProfit: null,
      trailingAbs: null,
      trailingRel: null,
      initialStopPrice: null,
    });

    this.form.updateValueAndValidity();
  };

  readonly #subscribeToOrderVolumeCalculation = (): void => {
    combineLatest([this.tradeService.quote$, this.form.valueChanges])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([quote, { execType, direction, quantityForm, optionsForm }]) => {
        this.tradeService.updateOrderVolume({
          quote,
          execType,
          direction,
          quantity: Number(quantityForm?.quantity),
          stopPrice: optionsForm?.stopPrice,
          stopLimitPrice: optionsForm?.stopLimitPrice,
          limitPrice: optionsForm?.limitPrice,
          stopLoss: optionsForm?.stopLoss,
          trailingAbs: optionsForm?.trailingAbs,
          trailingRel: optionsForm?.trailingRel,
          trailingType: optionsForm?.trailingType,
          initialStopPrice: optionsForm?.initialStopPrice,
        });
      });
  };

  readonly #setAdditionalAssetValidators = (asset: Asset) => {
    const validatorFn = this.tradeCreateValidators.validateActiveTradingHours();
    if (asset.instrumentType !== InstrumentType.Crypto) {
      this.form.addValidators(validatorFn);
    } else {
      this.form.removeValidators(validatorFn);
    }
    this.form.updateValueAndValidity();
  };
}
